import React from "react";
import { Marker, Polyline } from "@react-google-maps/api";

export const MAP_LIBRARIES = ["geometry", "drawing"];
export const GPS_COLOR = "#3182CE";
export const TOWER_COLOR = "#16a34a";
const BATTERY_LOW = 20;

const svgIcon = (markup) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markup)}`;

export const START_FLAG = svgIcon(`<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
  <path d="M8 34V3" stroke="#111827" stroke-width="2" stroke-linecap="round"/>
  <path d="M9 4h14l-5 6 5 6H9z" fill="#16a34a" stroke="#14532d" stroke-width="1"/>
</svg>`);

export const END_FLAG = svgIcon(`<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
  <path d="M8 34V3" stroke="#111827" stroke-width="2" stroke-linecap="round"/>
  <rect x="9" y="4" width="7" height="6" fill="#111827"/>
  <rect x="16" y="4" width="7" height="6" fill="#f9fafb" stroke="#111827"/>
  <rect x="9" y="10" width="7" height="6" fill="#f9fafb" stroke="#111827"/>
  <rect x="16" y="10" width="7" height="6" fill="#111827"/>
</svg>`);

const BATTERY_FLAG = svgIcon(`<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
  <circle cx="11" cy="11" r="10" fill="#b91c1c" stroke="#fff" stroke-width="2"/>
  <rect x="6" y="8" width="8" height="6" rx="1" fill="none" stroke="#fff" stroke-width="1.2"/>
  <rect x="14" y="10" width="1.4" height="2" fill="#fff"/>
</svg>`);

const EVENT_FLAGS = {
  2: { label: "Power connected", color: "#ca8a04" },
  15: { label: "Movement started", color: "#2563eb" },
  16: { label: "Halted / idling", color: "#ea580c" },
  17: { label: "Halt and sleep", color: "#6d28d9" },
  8: { label: "Critical motion", color: "#dc2626" },
};

const LEGEND_ITEMS = [8, 15, 16, 17, 2].map((code) => EVENT_FLAGS[code]);

function eventCode(point) {
  const raw = point?.eventcode != null && point.eventcode !== "" ? point.eventcode : point?.event;
  if (raw == null || raw === "") return null;
  const text = String(raw).trim();
  const numeric = Number(text);
  if (!Number.isFinite(numeric) || String(numeric) !== text.replace(/\.0$/, "")) return null;
  return numeric;
}

function classifyEvent(point) {
  const code = eventCode(point);
  if (code === 5 || code === 7 || code === 0) return null;
  if (code != null) return EVENT_FLAGS[code] ? { ...EVENT_FLAGS[code], code } : null;
  const name = String(point?.event ?? "").trim().toLowerCase();
  if (!name || name === "normal" || name.includes("periodic")) return null;
  if (name.includes("power") && name.includes("connect")) return { ...EVENT_FLAGS[2], code: 2 };
  if (name.includes("movement") && name.includes("start")) return { ...EVENT_FLAGS[15], code: 15 };
  if (name.includes("idling") || name.includes("halted") || (name.includes("movement") && name.includes("halt"))) return { ...EVENT_FLAGS[16], code: 16 };
  if (name.includes("sleep")) return { ...EVENT_FLAGS[17], code: 17 };
  if (name.includes("critical") || (name.includes("wake") && name.includes("motion"))) return { ...EVENT_FLAGS[8], code: 8 };
  return null;
}

function batteryLevel(point) {
  const number = Number(String(point?.batstat ?? point?.batterystatus ?? "").replace("%", "").trim());
  return Number.isFinite(number) ? number : null;
}

export function isTower(point) {
  return String(point?.postype ?? point?.positiontype ?? "").trim().toUpperCase() === "TOWER";
}

function coordinatePair(lat, lng) {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || (latitude === 0 && longitude === 0)) return null;
  return { lat: latitude, lng: longitude };
}

export const MAP_FALLBACK_CENTER = { lat: 20.5937, lng: 78.9629 };

export function pointPosition(point) {
  if (!point) return null;
  return coordinatePair(point.lat ?? point.latitude, point.lng ?? point.longitude);
}

export function latestPosition(points) {
  const list = points || [];
  for (let index = list.length - 1; index >= 0; index -= 1) {
    const position = pointPosition(list[index]);
    if (position) return { position, point: list[index] };
  }
  return null;
}

export function buildRoute(mapInfo) {
  const segments = [];
  const flags = [];
  let wasLow = false;
  (mapInfo || []).forEach((point, index) => {
    const position = pointPosition(point);
    if (!position) return;
    const tower = isTower(point);
    const previous = segments[segments.length - 1];
    if (!previous || previous.tower !== tower) {
      const path = previous ? [previous.path[previous.path.length - 1], position] : [position];
      segments.push({ tower, path });
    } else {
      previous.path.push(position);
    }
    const kind = classifyEvent(point);
    const level = batteryLevel(point);
    let dropped = false;
    if (level != null) {
      const low = level < BATTERY_LOW;
      dropped = low && !wasLow;
      wasLow = low;
    }
    if (kind || dropped) {
      flags.push({ id: `flag-${index}`, position, point, kind, batteryLow: dropped, level });
    }
  });
  return { segments, flags };
}

export const RouteLines = React.memo(function RouteLines({ segments }) {
  return segments.map((segment, index) => (
    <Polyline
      key={`${segment.tower ? "tower" : "gps"}-${index}`}
      path={segment.path}
      options={{
        strokeColor: segment.tower ? TOWER_COLOR : GPS_COLOR,
        strokeOpacity: 0.95,
        strokeWeight: 4,
      }}
    />
  ));
});

export const RouteFlags = React.memo(function RouteFlags({ flags, onSelect }) {
  return flags.map((flag) => (
    <Marker
      key={flag.id}
      position={flag.position}
      zIndex={6}
      title={flag.kind ? flag.kind.label : "Battery low"}
      icon={
        flag.kind
          ? {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: flag.kind.color,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            }
          : { url: BATTERY_FLAG, scaledSize: new window.google.maps.Size(22, 22), anchor: new window.google.maps.Point(11, 11) }
      }
      onClick={() => onSelect(flag)}
    />
  ));
});

export function RouteLegend() {
  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-[1] max-w-[200px] rounded-md bg-white/95 px-3 py-2 text-xs text-navy-700 shadow">
      <div className="flex items-center gap-2"><span className="inline-block h-1.5 w-5 rounded" style={{ background: GPS_COLOR }} /> GPS</div>
      <div className="mt-1 flex items-center gap-2"><span className="inline-block h-1.5 w-5 rounded" style={{ background: TOWER_COLOR }} /> Tower</div>
      {LEGEND_ITEMS.map((item) => (
        <div key={item.label} className="mt-1 flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: item.color }} />
          {item.label}
        </div>
      ))}
    </div>
  );
}
